import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, MapPin, ArrowRight, Loader2, CheckCircle2, Globe2, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { FourStars, StarsDivider } from "../components/FourStars";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function MapPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ provinces: [], diaspora: [], totals: { total_members: 0 } });
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joinedSections, setJoinedSections] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sections");
      setData(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleJoin = async (slug) => {
    if (!user) {
      toast.error("Connectez-vous pour rejoindre une section");
      return;
    }
    setJoining(true);
    try {
      await api.post(`/sections/${slug}/join`);
      setJoinedSections((s) => new Set([...s, slug]));
      toast.success("Vous avez rejoint cette section !");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setJoining(false);
    }
  };

  // Sort provinces by member count for podium
  const podium = useMemo(() => {
    return [...data.provinces].sort((a, b) => b.members - a.members).slice(0, 5);
  }, [data.provinces]);

  return (
    <div data-testid="map-page" className="min-h-screen bg-[#FAFAFA]">
      {/* Hero */}
      <section className="relative py-20 md:py-24 text-white overflow-hidden bg-gradient-to-br from-[#0A0F1A] via-[#0052CC] to-[#0A0F1A]">
        <div className="absolute inset-0 stars-pattern opacity-15" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <FourStars size={14} className="justify-center mb-4" />
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Présence nationale & diaspora</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-tight">
            La carte des <span className="text-[#D4AF37]">A.F.B.</span>
          </h1>
          <p className="text-white/80 mt-5 max-w-2xl mx-auto leading-relaxed">
            {data.totals.total_members.toLocaleString("fr-FR")} membres mobilisés à travers les 26 provinces de la RDC et 6 sections diaspora.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Carte SVG stylisée */}
            <div className="lg:col-span-2 bg-white rounded-3xl border border-black/5 shadow-lg p-6 md:p-10" data-testid="drc-map">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">République Démocratique du Congo</div>
                  <h2 className="font-display text-2xl text-[#0A0F1A] mt-1">Carte interactive — 26 provinces</h2>
                </div>
                <FourStars size={12} />
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-32"><Loader2 className="text-[#0052CC] animate-spin" size={36} /></div>
              ) : (
                <div className="relative aspect-[1/1] md:aspect-[5/4] bg-gradient-to-br from-[#F5F5F0] to-[#EFEAE0] rounded-2xl border border-[#D4AF37]/30 overflow-hidden">
                  {/* Decorative compass */}
                  <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.3em] text-[#475569]">N ↑</div>
                  {/* Equator line */}
                  <div className="absolute left-0 right-0 top-[35%] border-t border-dashed border-[#D4AF37]/40" />

                  {/* Province nodes */}
                  {data.provinces.map((p) => {
                    const isHovered = hovered === p.slug;
                    const isSelected = selected?.slug === p.slug;
                    const isCapital = p.type === "capital";
                    const radius = isCapital ? 16 : 11;
                    return (
                      <button
                        key={p.slug}
                        onMouseEnter={() => setHovered(p.slug)}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => setSelected(p)}
                        data-testid={`province-${p.slug}`}
                        className={`absolute group transition-all ${isSelected ? "z-30" : isHovered ? "z-20" : "z-10"}`}
                        style={{ left: `${p.x}%`, top: `${p.y}%`, transform: "translate(-50%, -50%)" }}
                        aria-label={`${p.name} — ${p.members} membres`}
                      >
                        <div
                          className={`rounded-full transition-all ${
                            isCapital
                              ? "bg-[#CE1126] ring-4 ring-[#CE1126]/20"
                              : isSelected
                              ? "bg-[#D4AF37] ring-4 ring-[#D4AF37]/30"
                              : isHovered
                              ? "bg-[#0052CC] ring-4 ring-[#0052CC]/20"
                              : "bg-[#0052CC]/80 ring-2 ring-[#0052CC]/15"
                          }`}
                          style={{ width: radius * 2, height: radius * 2 }}
                        />
                        <span className={`absolute left-1/2 -translate-x-1/2 top-full mt-1 text-[10px] font-semibold whitespace-nowrap transition-all ${
                          isHovered || isSelected || isCapital ? "opacity-100 text-[#0A0F1A]" : "opacity-0 group-hover:opacity-100 text-[#475569]"
                        }`}>
                          {p.name}
                        </span>
                      </button>
                    );
                  })}

                  {/* Tooltip on hover */}
                  {hovered && (() => {
                    const p = data.provinces.find((x) => x.slug === hovered);
                    if (!p) return null;
                    return (
                      <div
                        className="absolute z-40 pointer-events-none px-3 py-2 rounded-md bg-[#0A0F1A] text-white text-xs shadow-xl"
                        style={{
                          left: `${p.x}%`, top: `${p.y}%`,
                          transform: "translate(-50%, -200%)",
                        }}
                      >
                        <div className="font-semibold">{p.name}</div>
                        <div className="text-[#D4AF37] text-[10px] uppercase tracking-wider mt-0.5">
                          {p.members.toLocaleString("fr-FR")} membres
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Légende */}
              <div className="flex flex-wrap items-center gap-5 mt-5 text-xs text-[#475569]">
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#CE1126]" /> Capitale Kinshasa</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#0052CC]" /> Provinces RDC</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#D4AF37]" /> Sélectionnée</span>
              </div>
            </div>

            {/* Panel droit : section sélectionnée + podium */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-black/5 shadow-lg p-6 min-h-[280px]" data-testid="section-detail">
                {selected ? (
                  <>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">{selected.type === "capital" ? "Capitale" : "Province"}</div>
                    <h3 className="font-display text-2xl text-[#0A0F1A] mt-1">{selected.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-[#475569] mt-1.5">
                      <MapPin size={13} className="text-[#0052CC]" /> Chef-lieu : {selected.city}
                    </div>
                    <div className="mt-5 p-5 rounded-xl bg-gradient-to-br from-[#0052CC]/10 to-[#D4AF37]/10 border border-[#D4AF37]/20">
                      <div className="text-xs uppercase tracking-[0.2em] text-[#475569]">Membres mobilisés</div>
                      <div className="font-display text-4xl text-[#0A0F1A] mt-1.5">
                        {selected.members.toLocaleString("fr-FR")}
                      </div>
                    </div>
                    {joinedSections.has(selected.slug) ? (
                      <div className="mt-5 flex items-center gap-2 px-4 py-3 rounded-md bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-sm text-[#0A0F1A]">
                        <CheckCircle2 size={16} className="text-[#D4AF37]" /> Vous avez rejoint cette section
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleJoin(selected.slug)}
                        disabled={joining || !user}
                        className="w-full mt-5 cta-glow-blue rounded-sm py-6 font-semibold uppercase tracking-wider"
                        data-testid="join-section-btn"
                      >
                        {joining ? <Loader2 size={16} className="animate-spin mr-2" /> : <Users size={16} className="mr-2" />}
                        Rejoindre cette section
                      </Button>
                    )}
                    {!user && (
                      <p className="text-xs text-[#475569] mt-3 text-center">
                        <Link to="/login" className="text-[#0052CC] font-semibold hover:underline">Connectez-vous</Link> pour rejoindre.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="text-center py-10">
                    <MapPin size={36} className="text-[#0052CC]/40 mx-auto" />
                    <p className="text-sm text-[#475569] mt-3">Cliquez sur une province pour découvrir sa section A.F.B.</p>
                  </div>
                )}
              </div>

              {/* Podium */}
              <div className="bg-[#0A0F1A] text-white rounded-2xl p-6 shadow-lg" data-testid="map-podium">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-4">
                  <Star size={11} fill="#D4AF37" stroke="#D4AF37" /> Top 5 sections
                </div>
                {podium.map((p, i) => (
                  <div key={p.slug} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
                    <span className="font-display text-lg text-[#D4AF37] w-6">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{p.name}</div>
                    </div>
                    <span className="text-xs text-white/65 font-mono">
                      {p.members.toLocaleString("fr-FR")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <StarsDivider className="my-16" />

          {/* Diaspora */}
          <div data-testid="diaspora-section">
            <div className="text-center mb-10">
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold flex items-center justify-center gap-2">
                <Globe2 size={13} /> Diaspora active
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-[#0A0F1A] mt-2">Sections internationales</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {data.diaspora.map((d) => (
                <motion.div
                  key={d.slug}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-xl border border-black/5 hover:border-[#D4AF37]/50 shadow-sm hover:shadow-xl p-5 text-center transition-all cursor-pointer"
                  onClick={() => handleJoin(d.slug)}
                  data-testid={`diaspora-${d.slug}`}
                >
                  <Globe2 size={22} className="text-[#0052CC] mx-auto" />
                  <div className="font-display text-base text-[#0A0F1A] mt-2.5">{d.name}</div>
                  <div className="text-[10px] uppercase tracking-wide text-[#475569] mt-0.5">{d.country}</div>
                  <div className="mt-3 text-xs font-mono text-[#0052CC]">
                    {d.members.toLocaleString("fr-FR")} membres
                  </div>
                  {joinedSections.has(d.slug) && (
                    <div className="text-[10px] mt-2 text-[#D4AF37] font-semibold uppercase">✓ Rejoint</div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
